import { useEffect, useMemo, useState } from 'react';
import { getPosts } from '../services/api';
import PostCard from './PostCard';
import useSeo from '../hooks/useSeo';

export default function TrendingNews({ selectedCountry = '', selectedPlace = '' }) {
  useSeo({
    title: 'Trending Community Reports',
    description: 'Explore the most discussed community updates and anonymous incident reports on TheScandal in real time.',
    path: '/trending',
    keywords: [
      'trending community reports',
      'most discussed incidents',
      'anonymous report trends',
      'live local updates',
      'whistleblower updates',
    ],
  });

  const [discussed, setDiscussed] = useState([]);
  const [liked, setLiked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      getPosts(1, 15, 'discussed', selectedCountry, selectedPlace),
      getPosts(1, 15, 'liked', selectedCountry, selectedPlace),
    ])
      .then(([discussedData, likedData]) => {
        if (cancelled) return;
        setDiscussed(discussedData.posts || []);
        setLiked(likedData.posts || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load trending news.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCountry, selectedPlace]);

  const trendingPosts = useMemo(() => {
    const byId = new Map();
    for (const post of [...discussed, ...liked]) {
      const id = post._id || post.id;
      if (!id) continue;
      const score = (post.commentsCount || 0) * 2 + (post.likes || 0);
      const prev = byId.get(id);
      if (!prev || score > prev.score) {
        byId.set(id, { post, score });
      }
    }

    return Array.from(byId.values())
      .sort((a, b) => b.score - a.score)
      .map((x) => x.post)
      .slice(0, 20);
  }, [discussed, liked]);

  return (
    <section className="trending-page">
      <header className="trending-header">
        <h1>Trending Updates</h1>
        <p>Live pulse from the most discussed community updates and conversations.</p>
      </header>

      {selectedCountry && (
        <p className="loading-text">Region: {selectedCountry}</p>
      )}
      {selectedPlace && (
        <p className="loading-text">Place filter: {selectedPlace}</p>
      )}

      {loading && <div className="spinner">Loading trending updates…</div>}

      {error && (
        <div className="error-banner">{error}</div>
      )}

      {!loading && !error && trendingPosts.length === 0 && (
        <div className="empty-state">
          <p>No trending posts yet for this filter.</p>
        </div>
      )}

      <div className="post-feed">
        {trendingPosts.map((post) => {
          const id = post._id || post.id;
          return <PostCard key={id} post={post} selectedCountry={selectedCountry} />;
        })}
      </div>
    </section>
  );
}
